import { pctToGate } from './sniperArm';
import type { PatchSniperMarketDto } from '../types/sniper.types';

/**
 * A patch to apply to every selected market — only the fields the operator typed.
 * DERIVED from the wire DTO (not hand-duplicated) so the bulk bar can never drift
 * from the contract it writes to.
 */
export type SniperBulkPatch = Pick<
  PatchSniperMarketDto,
  | 'size'
  | 'maxPrice'
  | 'varExitPrice'
  | 'varDefenseEnabled'
  | 'varPreventiveExitEnabled'
  | 'varConfirmedFloorPrice'
  | 'varRebuyEnabled'
  | 'autoNoEnabled'
  | 'noMaxPrice'
  | 'confidenceGate'
  | 'enabled'
>;

export interface ParsedBulkPatch {
  patch: SniperBulkPatch;
  /** At least one typed field is valid AND none typed is invalid. */
  canApply: boolean;
}

/**
 * Parse the bulk-bar draft strings into a patch carrying ONLY the params the
 * operator typed and that are valid. A blank field is omitted (leave untouched).
 * Validation mirrors `SniperMarketRow` AND the backend DTO: size >= 1 (server is
 * `@Min(1)` — accepting a fractional size here only earns a 400 on the whole
 * bulk), maxPrice, varExitPrice and varConfirmedFloorPrice ∈ (0,1], gate a
 * percent 1..100 (→ 0..1 via `pctToGate`). The three VAR-defense switches
 * (`varDefenseEnabled` / `varPreventiveExitEnabled` / `varRebuyEnabled` /
 * `autoNoEnabled`) are tri-state on the wire: `''` = leave untouched,
 * `'true'`/`'false'` = set the boolean; any other string is invalid. Any
 * typed-but-invalid field blocks Apply so a bad value can't silently skip.
 * `noMaxPrice` (the auto-NO ceiling) validates like `maxPrice` — (0,1].
 */
export function parseBulkPatch(
  size: string,
  maxPrice: string,
  varExitPrice: string,
  gate: string,
  varDefenseEnabled: string = '',
  varPreventiveExitEnabled: string = '',
  varConfirmedFloorPrice: string = '',
  varRebuyEnabled: string = '',
  autoNoEnabled: string = '',
  noMaxPrice: string = '',
): ParsedBulkPatch {
  const patch: SniperBulkPatch = {};
  let invalid = false;
  let any = false;

  if (size !== '') {
    const s = Number(size);
    if (Number.isFinite(s) && s >= 1) {
      patch.size = s;
      any = true;
    } else invalid = true;
  }
  if (maxPrice !== '') {
    const m = Number(maxPrice);
    if (Number.isFinite(m) && m > 0 && m <= 1) {
      patch.maxPrice = m;
      any = true;
    } else invalid = true;
  }
  if (varExitPrice !== '') {
    const v = Number(varExitPrice);
    if (Number.isFinite(v) && v > 0 && v <= 1) {
      patch.varExitPrice = v;
      any = true;
    } else invalid = true;
  }
  // Phase-2 aggressive-sell floor — a price like varExitPrice, so the SAME (0,1]
  // rule as the server's.
  if (varConfirmedFloorPrice !== '') {
    const f = Number(varConfirmedFloorPrice);
    if (Number.isFinite(f) && f > 0 && f <= 1) {
      patch.varConfirmedFloorPrice = f;
      any = true;
    } else invalid = true;
  }
  // Auto-NO ceiling — a price like maxPrice/varExitPrice, same (0,1] rule.
  if (noMaxPrice !== '') {
    const n = Number(noMaxPrice);
    if (Number.isFinite(n) && n > 0 && n <= 1) {
      patch.noMaxPrice = n;
      any = true;
    } else invalid = true;
  }
  // Tri-state booleans: '' leaves untouched, only 'true'/'false' set the flag.
  const bools: Array<
    [
      string,
      (
        | 'varDefenseEnabled'
        | 'varPreventiveExitEnabled'
        | 'varRebuyEnabled'
        | 'autoNoEnabled'
      ),
    ]
  > = [
    [varDefenseEnabled, 'varDefenseEnabled'],
    [varPreventiveExitEnabled, 'varPreventiveExitEnabled'],
    [varRebuyEnabled, 'varRebuyEnabled'],
    [autoNoEnabled, 'autoNoEnabled'],
  ];
  for (const [raw, key] of bools) {
    if (raw !== '') {
      if (raw === 'true' || raw === 'false') {
        patch[key] = raw === 'true';
        any = true;
      } else invalid = true;
    }
  }
  if (gate !== '') {
    const g = pctToGate(Number(gate)); // 1..100 → 0..1, else null
    if (g !== null) {
      patch.confidenceGate = g;
      any = true;
    } else invalid = true;
  }

  return { patch, canApply: any && !invalid };
}
